/**
 * Session keepalive + idle/grace model.
 *
 * Heartbeat: 2-min interval POST/GET to /api/session/heartbeat.
 * Idle: after IDLE_THRESHOLD_MS of inactivity, pause heartbeat + show warning dialog.
 * Grace: after IDLE_GRACE_MS, best-effort logout POST + redirect to landing.
 * handleUnauthorized: single shared 401 invalidation route (OWASP A07).
 */

const HEARTBEAT_URL = "/api/session/heartbeat";
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

export const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
export const IDLE_GRACE_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS: ReadonlyArray<string> = [
  "mousemove",
  "keydown",
  "touchstart",
  "click",
  "scroll",
];

// --- State ---
let activityListenersBound = false;
let userWasActive = false;
let heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
let idleTimerId: ReturnType<typeof setTimeout> | null = null;
let graceTimerId: ReturnType<typeof setTimeout> | null = null;
let hiddenAt: number | null = null;
let loggingOut = false;

// --- Heartbeat ---

function onActivity(): void {
  userWasActive = true;
}

async function sendHeartbeat(): Promise<void> {
  if (!userWasActive) return;

  try {
    const response = await fetch(HEARTBEAT_URL, { credentials: "include" });
    if (response.status === 401) {
      handleUnauthorized();
      return;
    }
    if (response.ok) {
      userWasActive = false;
    }
  } catch {
    // Network error — keep going, don't break session
  }
}

function addHeartbeatListeners(): void {
  for (const event of ACTIVITY_EVENTS) {
    document.addEventListener(event, onActivity, { passive: true });
  }
}

function removeHeartbeatListeners(): void {
  for (const event of ACTIVITY_EVENTS) {
    document.removeEventListener(event, onActivity);
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") {
    if (heartbeatIntervalId !== null) {
      clearInterval(heartbeatIntervalId);
      heartbeatIntervalId = null;
    }
    removeHeartbeatListeners();
    hiddenAt = Date.now();
  } else {
    addHeartbeatListeners();
    heartbeatIntervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    if (
      userWasActive &&
      hiddenAt !== null &&
      Date.now() - hiddenAt >= HEARTBEAT_INTERVAL_MS
    ) {
      sendHeartbeat();
    }
    hiddenAt = null;
  }
}

export function startSessionKeepalive(): void {
  if (heartbeatIntervalId !== null) return;
  userWasActive = false;
  hiddenAt = null;
  addHeartbeatListeners();
  heartbeatIntervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

export function stopSessionKeepalive(): void {
  if (heartbeatIntervalId !== null) {
    clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
  removeHeartbeatListeners();
  document.removeEventListener("visibilitychange", onVisibilityChange);
  userWasActive = false;
  hiddenAt = null;
}

// --- Idle / Grace ---

function onActivityReset(): void {
  resetIdleTimer();
}

export function resetIdleTimer(): void {
  if (idleTimerId !== null) {
    clearTimeout(idleTimerId);
  }
  idleTimerId = setTimeout(onIdle, IDLE_THRESHOLD_MS);
}

export function onIdle(): void {
  stopSessionKeepalive();
  showIdleWarning();
}

export function showIdleWarning(): void {
  const dialog = document.getElementById("idle-warning");
  if (
    dialog instanceof HTMLDialogElement &&
    typeof dialog.showModal === "function" &&
    !dialog.open
  ) {
    dialog.showModal();
  }
  if (graceTimerId !== null) {
    clearTimeout(graceTimerId);
  }
  graceTimerId = setTimeout(onIdleGraceExpired, IDLE_GRACE_MS);
}

export function hideIdleWarning(): void {
  const dialog = document.getElementById("idle-warning");
  if (dialog instanceof HTMLDialogElement && dialog.open) {
    dialog.close();
  }
  if (graceTimerId !== null) {
    clearTimeout(graceTimerId);
    graceTimerId = null;
  }
}

export function onIdleGraceExpired(): void {
  hideIdleWarning();
  stopActivityTracking();
  loggingOut = true;

  // Best-effort logout with keepalive before navigation
  try {
    fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // fire-and-forget
  }

  window.location.href = "/?session_expired=1";
}

export async function stayActiveAfterHeartbeat(): Promise<void> {
  hideIdleWarning();

  try {
    const response = await fetch(HEARTBEAT_URL, { credentials: "include" });
    if (response.status === 401) {
      handleUnauthorized();
      return;
    }
  } catch {
    // Network error — still resume below
  }

  // Guard: if logout happened during in-flight heartbeat, don't restart
  if (loggingOut) return;

  startSessionKeepalive();
  resetIdleTimer();
}

export function startActivityTracking(): void {
  loggingOut = false;
  if (activityListenersBound) {
    resetIdleTimer();
    return;
  }
  activityListenersBound = true;
  for (const event of ACTIVITY_EVENTS) {
    document.addEventListener(event, onActivityReset, { passive: true });
  }
  resetIdleTimer();
}

export function stopActivityTracking(): void {
  activityListenersBound = false;
  for (const event of ACTIVITY_EVENTS) {
    document.removeEventListener(event, onActivityReset);
  }
  if (idleTimerId !== null) {
    clearTimeout(idleTimerId);
    idleTimerId = null;
  }
  if (graceTimerId !== null) {
    clearTimeout(graceTimerId);
    graceTimerId = null;
  }
}

// --- Shared 401 handler (security F1) ---

export function handleUnauthorized(): void {
  stopSessionKeepalive();
  stopActivityTracking();
  loggingOut = true;
  window.location.href = "/?session_expired=1";
}
